// Recurring-bill detection for households (spec 2026-07-15, stretch layer).
//
// The idea: rent, PG&E, Netflix hit the statement every month with roughly
// the same amount. If we spot that shape, we SUGGEST "make this a recurring
// bill" — never auto-create. One tap pre-fills the RecurringExpenses form.
import { displayName } from './expenseDisplay'

// Same normalization as the backend's merchant_key and TransactionsPage's
// "select all from merchant": first 3 lowercase words.
export function normalizeMerchantKey(description) {
  return (description || '').toLowerCase().trim().split(/\s+/).slice(0, 3).join(' ')
}

// Max allowed spread between a merchant's smallest and largest amount for it
// to still "look like" the same monthly bill (utilities wobble a little).
const MAX_AMOUNT_RATIO = 1.15

/**
 * Find merchants that look like monthly bills.
 *
 * Rules: non-excluded rows only; same merchant key in ≥2 DISTINCT months;
 * amounts within ±15% of each other. Returns one suggestion per merchant,
 * built from its latest occurrence: { txnId, name, amount, day, merchantKey }.
 */
export function detectRecurringCandidates(transactions) {
  const byKey = new Map()
  for (const t of transactions) {
    if (t.status === 'excluded') continue
    if (!(t.amount > 0) || !t.posted_date) continue
    const key = normalizeMerchantKey(t.description_raw)
    if (!key) continue
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(t)
  }

  const suggestions = []
  for (const [key, txns] of byKey) {
    const months = new Set(txns.map(t => t.posted_date.slice(0, 7))) // "YYYY-MM"
    if (months.size < 2) continue
    const amounts = txns.map(t => t.amount)
    if (Math.max(...amounts) / Math.min(...amounts) > MAX_AMOUNT_RATIO) continue
    // Latest occurrence carries the freshest amount + billing day.
    const latest = [...txns].sort((a, b) => a.posted_date.localeCompare(b.posted_date)).pop()
    suggestions.push({
      txnId: latest.id,
      merchantKey: key,
      name: displayName(latest),
      amount: latest.amount,
      day: parseInt(latest.posted_date.slice(8, 10), 10) || 1,
    })
  }
  return suggestions
}
